import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { Search, X, ShoppingBag } from "lucide-react";
import api from "@/lib/api";
import { formatProductName, shopifyImg } from "@/lib/format";

export default function SearchOverlay({ isOpen, onClose }) {
  const [query, setQuery] = useState("");
  const [products, setProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      api.get("/shopify/products").then(({ data }) => setAllProducts(data || [])).catch(() => {});
      setTimeout(() => inputRef.current?.focus(), 100);
    } else { setQuery(""); setProducts([]); }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  const handleSearch = useCallback((term) => {
    setQuery(term);
    if (!term.trim()) { setProducts([]); return; }
    const lower = term.toLowerCase();
    setProducts(allProducts.filter(p => p.name?.toLowerCase().includes(lower) || p.category?.toLowerCase().includes(lower)).slice(0, 6));
  }, [allProducts]);

  if (!isOpen) return null;

  return (
    <>
      <div data-testid="search-overlay-backdrop" className="fixed inset-0 bg-black/80 z-[80]" onClick={onClose} />
      <div data-testid="search-overlay" className="fixed top-0 left-0 right-0 bg-[#0A0A0A] z-[90] border-b border-[#8A8A8A]/20">
        <div className="max-w-3xl mx-auto px-6 py-8">
          <div className="flex items-center gap-4">
            <Search size={18} className="text-[#8A8A8A] flex-shrink-0" />
            <input ref={inputRef} data-testid="search-input" type="text" value={query} onChange={(e) => handleSearch(e.target.value)} placeholder="SEARCH" className="flex-1 text-base uppercase tracking-[0.15em] outline-none bg-transparent text-[#F2F0EB] placeholder:text-[#8A8A8A]" />
            <button data-testid="search-close" onClick={onClose} className="p-1 text-[#8A8A8A] hover:text-[#F2F0EB] transition-colors"><X size={18} /></button>
          </div>
          {query.trim() && (
            <div className="mt-6 border-t border-[#8A8A8A]/15 pt-4">
              {products.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {products.map((p) => (
                    <Link key={p.id} to={`/products/${p.id}`} onClick={onClose} data-testid={`search-result-${p.id}`} className="flex items-center gap-4 p-3 hover:bg-[#F2F0EB]/5 transition-colors">
                      <div className="w-12 h-12 bg-[#141414] flex-shrink-0 overflow-hidden">
                        {p.images?.[0] ? <img src={shopifyImg(p.images[0], 120)} alt={p.name} width="48" height="48" loading="lazy" className="w-full h-full object-cover" /> : <ShoppingBag size={14} className="text-[#8A8A8A]/50" />}
                      </div>
                      <div className="min-w-0">
                        <h4 className="font-['Oswald'] text-xs uppercase tracking-[0.12em] text-[#F2F0EB] truncate">{formatProductName(p)}</h4>
                        <p className="text-sm text-[#8A8A8A]">${p.price?.toFixed(2)}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-[#8A8A8A] py-4">No results for "{query}"</p>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
